import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { analyticsEvents } from "@/services/analytics";
import { Store } from "@/types/stores";

interface PaymentModeCardProps {
  store: Store;
  onUpdate: () => void;
  isLoading: boolean;
}

export function PaymentModeCard({
  store,
  onUpdate,
  isLoading,
}: PaymentModeCardProps) {
  const { t } = useTranslation();
  const [paymentMode, setPaymentMode] = useState<"BeforeOrder" | "AfterOrder">(
    store?.request_payment_mode || "AfterOrder"
  );

  const handlePaymentModeChange = async (
    newMode: "BeforeOrder" | "AfterOrder"
  ) => {
    try {
      const { error } = await supabase
        .from("stores")
        .update({ request_payment_mode: newMode })
        .eq("id", store.id);

      if (error) throw error;

      setPaymentMode(newMode);
      onUpdate();
      toast.success(t("settingsUpdatedSuccessfully"));

      // Track the change
      analyticsEvents.settings.preferenceChanged("payment_mode", newMode);
    } catch (error) {
      console.error("Error updating payment mode:", error);
      toast.error(t("errorUpdatingPreference"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <CreditCard className="h-5 w-5" />
          <CardTitle>{t("paymentSettings.title")}</CardTitle>
        </div>
        <CardDescription>{t("paymentSettings.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup
          value={paymentMode}
          onValueChange={handlePaymentModeChange}
          className="space-y-4"
          disabled={isLoading}
        >
          <div className="flex flex-col space-y-1">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="BeforeOrder" id="before-order" />
              <Label htmlFor="before-order" className="font-medium">
                {t("paymentSettings.beforeOrder")}
              </Label>
            </div>
            <p className="text-sm text-muted-foreground ml-6">
              {t("paymentSettings.beforeOrderDescription")}
            </p>
          </div>

          <div className="flex flex-col space-y-1">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="AfterOrder" id="after-order" />
              <Label htmlFor="after-order" className="font-medium">
                {t("paymentSettings.afterOrder")}
              </Label>
            </div>
            <p className="text-sm text-muted-foreground ml-6">
              {t("paymentSettings.afterOrderDescription")}
            </p>
          </div>
        </RadioGroup>
      </CardContent>
    </Card>
  );
}
